import type Location from '@/data/location/model';
import {
  RIPPLING_SCHEDULE_DELETE_ERROR_CODE,
  RIPPLING_SCHEDULE_DELETE_PAY_RUN_ERROR_CODE,
} from '@/integrations/modules/rippling/consts';
import Monolith from '@/shared/api/monolith';
import { parseAPIError } from '@/shared/redux/helpers/parseApiError';
import { createWiwAsyncThunk } from '@/store';

type DeletedLocationResponse = {
  success: boolean;
  deleted_location: number;
};

export const deleteLocation = createWiwAsyncThunk<DeletedLocationResponse, Location>(
  'location/delete',
  async (location, { rejectWithValue }) => {
    try {
      return await Monolith.url(`/locations/${location.id}`).delete();
    } catch (error: any) {
      const parsed = parseAPIError(error);

      if (
        parsed.code === RIPPLING_SCHEDULE_DELETE_PAY_RUN_ERROR_CODE ||
        parsed.code === RIPPLING_SCHEDULE_DELETE_ERROR_CODE
      ) {
        return rejectWithValue({
          code: parsed.code,
          message: parsed.message,
        });
      }
      return rejectWithValue(parsed);
    }
  },
);

type BatchDeleteResult = {
  success?: boolean;
  id?: number;
  deleted_location?: number;
  error?: string;
  code?: number;
};

type BatchDeleteRejectValue = {
  code: number;
  message: string;
  failedLocations: Location[];
  succeededLocations: Location[];
};

export const deleteLocations = createWiwAsyncThunk<
  BatchDeleteResult[],
  Location[],
  { rejectValue: BatchDeleteRejectValue }
>('location/deleteMulti', async (locations, { rejectWithValue }) => {
  try {
    return await Monolith.url('/locations')
      .query({
        ids: locations.map(location => location.id),
      })
      .delete();
  } catch (error) {
    const resultError = parseDeleteLocationsResponse(error, locations);
    return rejectWithValue(resultError);
  }
});

function parseDeleteLocationsResponse(error: any, locations: Location[]): BatchDeleteRejectValue {
  const body: BatchDeleteResult[] | undefined = error?.json;

  if (!body || !Array.isArray(body)) {
    return {
      code: 0,
      message: 'Oops, something went wrong on our end. Try again in a few minutes.',
      failedLocations: [],
      succeededLocations: [],
    };
  }

  const locationById = new Map(locations.map(location => [location.id, location]));
  const failedLocations: Location[] = [];
  const succeededLocations: Location[] = [];
  let errorCode = 0;

  body.forEach(result => {
    const id = result.deleted_location || result.id;
    const location = id ? locationById.get(id) : undefined;

    if (!location) {
      return;
    }
    if (result.error) {
      failedLocations.push(location);
    } else if (result.success) {
      succeededLocations.push(location);
    }
  });

  if (failedLocations.length > 0) {
    const hasRipplingPayRunFailure = body.some(r => r.error && r.code === RIPPLING_SCHEDULE_DELETE_PAY_RUN_ERROR_CODE);
    const hasRipplingFailure = body.some(r => r.error && r.code === RIPPLING_SCHEDULE_DELETE_ERROR_CODE);

    if (hasRipplingPayRunFailure) {
      errorCode = RIPPLING_SCHEDULE_DELETE_PAY_RUN_ERROR_CODE;
    }
    if (hasRipplingFailure) {
      // General failure will have higher precedence
      errorCode = RIPPLING_SCHEDULE_DELETE_ERROR_CODE;
    }
  }

  return {
    code: errorCode,
    message: 'Some schedules could not be deleted.',
    failedLocations,
    succeededLocations,
  };
}
